import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

function isSchemaError(error) {
  if (!error) return false
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2)
    if (errorClass === '42') return true
    if (errorClass === '08') return true
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ]
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        
        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error loading profile:', error?.message)
            throw error
          } else if (error?.code === 'PGRST116') {
            console.log('No profile found for user')
          } else {
            console.log('Profile load error:', error?.message)
          }
        } else {
          setUserProfile(data)
          
          // Update last_sign_in_at
          await supabase?.from('user_profiles')?.update({ last_sign_in_at: new Date()?.toISOString() })?.eq('id', userId)
        }
      } catch (error) {
        if (isSchemaError(error)) {
          throw error
        }
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  const authStateHandlers = {
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id)
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Email/Password Sign In
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during sign in:', error?.message)
          throw error
        }
        return { data: null, error }
      }
      return { data, error: null }
    } catch (error) {
      if (isSchemaError(error)) throw error
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  // Email/Password Sign Up
  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          },
          emailRedirectTo: `${window.location?.origin}/auth/callback`
        }
      })
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during sign up:', error?.message)
          throw error
        }
        return { data: null, error }
      }
      return { data, error: null }
    } catch (error) {
      if (isSchemaError(error)) throw error
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  // Sign Out
  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      if (error && isSchemaError(error)) {
        console.error('Schema error during sign out:', error?.message)
        throw error
      }
      return { error }
    } catch (error) {
      if (isSchemaError(error)) throw error
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  // Reset Password
  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/auth/reset-password`
      })
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during password reset:', error?.message)
          throw error
        }
        return { data: null, error }
      }
      return { data, error: null }
    } catch (error) {
      if (isSchemaError(error)) throw error
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  // Update Password
  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      })
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error during password update:', error?.message)
          throw error
        }
        return { data: null, error }
      }
      return { data, error: null }
    } catch (error) {
      if (isSchemaError(error)) throw error
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  // Update Profile
  const updateProfile = async (updates) => {
    if (!user) return { data: null, error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error updating profile:', error?.message)
          throw error
        }
        return { data: null, error }
      }
      
      setUserProfile(data)
      return { data, error: null }
    } catch (error) {
      if (isSchemaError(error)) throw error
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  // Verify Invitation
  const verifyInvitation = async (token) => {
    try {
      const { data, error } = await supabase?.from('invitations')?.select('*')?.eq('invitation_token', token)?.eq('status', 'pending')?.single()
      
      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error verifying invitation:', error?.message)
          throw error
        }
        return { data: null, error }
      }
      
      // Check if invitation is expired
      if (new Date(data?.expires_at) < new Date()) {
        return { data: null, error: { message: 'Invitation has expired' } }
      }
      
      return { data, error: null }
    } catch (error) {
      if (isSchemaError(error)) throw error
      return { data: null, error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    isAuthenticated: !!user,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    verifyInvitation
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
